// Retry utilities for handling network operations and service initialization
// Provides reusable retry logic with proper error classification

import { RETRY_CONFIG, type RetryConfig } from '../../config/retryConfig';
import { logger } from '../core/logger';

// Custom error types for better error classification
export class RetryableError extends Error {
    constructor(
        message: string,
        public readonly originalError: Error
    ) {
        super(message);
        this.name = 'RetryableError';
    }
}

export class NonRetryableError extends Error {
    constructor(
        message: string,
        public readonly originalError?: Error
    ) {
        super(message);
        this.name = 'NonRetryableError';
    }
}

/** Thrown when a fetch is aborted due to navigation or component unmount — not a real error. */
export class RequestCancelledError extends NonRetryableError {
    constructor(public readonly duration: number) {
        super(`Request cancelled (navigation or unmount after ${duration}ms)`);
        this.name = 'RequestCancelledError';
    }
}

/**
 * True for any native error, also on a host page that replaced window.Error: its
 * prototype is then missing from native errors' chain, so `instanceof Error` alone
 * is false for them (ROSE-FRONT-1YG). Plain objects with a `message` stay false.
 */
const isError = (error: unknown): error is Error =>
    error instanceof Error ||
    ['[object Error]', '[object DOMException]'].includes(Object.prototype.toString.call(error));

/**
 * Extract a meaningful error message from any thrown value.
 * Handles plain objects (e.g. Supabase PostgrestError) that have a `message`
 * property but do NOT extend Error, avoiding the "[object Object]" trap.
 */
export const extractErrorMessage = (error: unknown): string => {
    if (error instanceof Error) return error.message;
    if (typeof error === 'string') return error;
    if (typeof error === 'object' && error !== null && 'message' in error) {
        const msg = (error as { message: unknown }).message;
        if (typeof msg === 'string') return msg;
    }
    try {
        const json = JSON.stringify(error);
        if (json !== '{}') return json;
    } catch {
        // circular reference or other serialization issue
    }
    return String(error);
};

// Classify errors to determine if they should be retried
export const isRetryableError = (error: unknown): boolean => {
    if (error instanceof RetryableError) {
        return true;
    }

    if (error instanceof NonRetryableError) {
        return false;
    }

    // AbortError means the request was cancelled (navigation, unmount, or signal abort).
    // The user is gone — retrying wastes resources and generates Sentry noise.
    if (isError(error) && error.name === 'AbortError') {
        return false;
    }

    const errorMessage = extractErrorMessage(error);

    // Network-related errors that are typically temporary
    const retryablePatterns = [
        // Fetch API errors
        'Failed to fetch',
        'fetch',
        'NetworkError',
        'ERR_NETWORK',
        'ERR_INTERNET_DISCONNECTED',
        'ERR_CONNECTION_REFUSED',
        'ECONNREFUSED',

        // Connection errors
        'Connection',
        'connection',
        'Connection refused',
        'Connection timed out',
        'Connection reset',

        // Timeout errors (real timeouts, not navigation aborts)
        'timeout',
        'Timeout',
        'Request timeout',

        // Server errors (5xx) that might be temporary
        'HTTP error! status: 5',
        'Internal Server Error',
        'Service Unavailable',
        'Bad Gateway',
        'Gateway Timeout',

        // Load errors
        'Load failed',
        'Loading failed',
    ];

    // Check if error matches retryable patterns
    const isRetryable = retryablePatterns.some((pattern) =>
        errorMessage.toLowerCase().includes(pattern.toLowerCase())
    );

    // Additional check for HTTP error status codes
    if (isError(error) && error.message.includes('HTTP error')) {
        const statusMatch = error.message.match(/status:\s*(\d+)/);
        if (statusMatch) {
            const status = parseInt(statusMatch[1], 10);
            // Retry on 5xx server errors and some 4xx errors
            return status >= 500 || status === 408 || status === 429;
        }
    }

    return isRetryable;
};

// Calculate exponential backoff delay with jitter
export const calculateRetryDelay = (
    attemptNumber: number,
    config: RetryConfig = RETRY_CONFIG
): number => {
    const delay = Math.min(
        config.baseDelay * config.backoffFactor ** attemptNumber,
        config.maxDelay
    );

    // Add jitter to prevent thundering herd (10% of delay)
    const jitter = delay * 0.1 * Math.random();
    return Math.floor(delay + jitter);
};

// Retry state interface for consistent state management
export interface RetryState {
    retryCount: number;
    isRetrying: boolean;
    nextRetryAt?: number;
    lastError?: string;
}

export const createInitialRetryState = (): RetryState => ({
    retryCount: 0,
    isRetrying: false,
    nextRetryAt: undefined,
    lastError: undefined,
});

// Retry operation executor with proper state management and cleanup
export const executeWithRetry = async <T>(
    operation: () => Promise<T>,
    options: {
        config?: RetryConfig;
        onRetry?: (attempt: number, error: Error, nextDelay: number) => void;
        onError?: (error: Error, attempt: number) => void;
        shouldRetry?: (error: unknown, attempt: number) => boolean;
    } = {}
): Promise<T> => {
    const config = options.config || RETRY_CONFIG;
    const shouldRetry = options.shouldRetry || isRetryableError;

    let lastError: Error | undefined;
    const cleanupFunctions: (() => void)[] = [];

    try {
        for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
            try {
                logger.debug(
                    `🔄 Executing operation (attempt ${attempt + 1}/${config.maxRetries + 1})`
                );
                return await operation();
            } catch (error) {
                // Rebuilding a native error drops its class, and callers classify by class.
                lastError = isError(error) ? error : new Error(extractErrorMessage(error));

                // If this is the last attempt or error is not retryable, throw
                if (attempt === config.maxRetries || !shouldRetry(error, attempt)) {
                    options.onError?.(lastError, attempt);
                    throw lastError;
                }

                // Calculate delay for next retry
                const delay = calculateRetryDelay(attempt, config);

                logger.debug(
                    `🔄 Operation failed (attempt ${attempt + 1}/${config.maxRetries + 1}), retrying in ${delay}ms`,
                    { error: lastError.message }
                );

                options.onRetry?.(attempt, lastError, delay);

                // Create timeout with cleanup capability
                const { promise, cleanup } = createTimeoutPromise(delay);
                cleanupFunctions.push(cleanup);

                // Wait before retry
                await promise;
            }
        }

        // This should never be reached due to the throw above, but TypeScript requires it
        throw lastError;
    } finally {
        // Clean up any pending timeouts
        cleanupFunctions.forEach((cleanup) => {
            cleanup();
        });
    }
};

// Helper function to format retry delay for user display
export const formatRetryDelay = (delayMs: number): string => {
    if (delayMs < 1000) {
        return `${Math.ceil(delayMs)}ms`;
    }
    return `${Math.ceil(delayMs / 1000)}s`;
};

// Helper function to create timeout promise for cleanup
export const createTimeoutPromise = (
    delayMs: number
): {
    promise: Promise<void>;
    cleanup: () => void;
} => {
    let timeoutId: NodeJS.Timeout;

    const promise = new Promise<void>((resolve) => {
        timeoutId = setTimeout(resolve, delayMs);
    });

    const cleanup = () => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
    };

    return { promise, cleanup };
};
